import { FirstPage, LastPage } from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardRounded';
import { Box, Button, Chip, CircularProgress, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { getAccessToken } from 'src/routes/AuthContext';
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { resetUpdateDocumentStatuses, updateDocumentStatusesAction } from 'src/redux/actions/physicianActions';
import physicianService, {
  PhysicianDocument,
  compositeWorkflowStatusFromDocument,
} from 'src/services/apiServices/physician-service/physician-service';
import { useNavigate } from 'react-router';
import SignatureModal from './components/SignatureModal';
import SigningLockedDialog from 'src/components/signature/signing-locked-dialog';

interface LocationState {
  selectedDocuments: PhysicianDocument[];
  source?: 'PHYSICIAN' | string;
}

interface RootState {
  getTherapistProfileReducer: {
    response: {
      data: {
        providerId: string;
      };
    };
  };
}

const PhysicianDocumentsSign: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { state } = useLocation() as { state: LocationState };

  const [currentDocIndex, setCurrentDocIndex] = useState(0);
  const [iframeRef, setIframeRef] = useState<HTMLIFrameElement | null>(null);
  const [approvedDocs, setApprovedDocs] = useState<Set<string>>(new Set());
  const [isSignModalOpen, setIsSignModalOpen] = useState(false);
  const [signatureName, setSignatureName] = useState('');
  const [isSigningDocuments, setIsSigningDocuments] = useState(false);
  const [documentsToSign, setDocumentsToSign] = useState<string[]>([]);
  const [physPinError, setPhysPinError] = useState<string | null>(null);
  const [physLocked, setPhysLocked] = useState<{ open: boolean; until: string | null }>({
    open: false,
    until: null,
  });

  const user = useSelector((state: RootState) => state.getTherapistProfileReducer?.response?.data);
  const updateDocumentStatuses = useSelector((state: any) => state.updateDocumentStatusesReducer);

  const selectedDocuments = useMemo(
    () => state?.selectedDocuments ?? [],
    [state?.selectedDocuments]
  );
  const currentDocument = useMemo(
    () => selectedDocuments[currentDocIndex],
    [selectedDocuments, currentDocIndex]
  );
  const isCurrentDocumentApproved = useMemo(
    () => currentDocument && approvedDocs.has(currentDocument.uuid),
    [currentDocument, approvedDocs]
  );

  const updateIFrameRef = useCallback((node: HTMLIFrameElement) => {
    setIframeRef(node);
  }, []);

  const handleNavigate = useCallback((index: number) => {
    setCurrentDocIndex(index);
  }, []);

  const handleApproveDocument = useCallback(async () => {
    if (!currentDocument || !user?.providerId) return;

    const documentsToUpdate = [currentDocument.uuid];
    dispatch(
      updateDocumentStatusesAction(user.providerId, documentsToUpdate, 'APPROVED', user.providerId)
    );

    setApprovedDocs((prev) => new Set([...prev, currentDocument.uuid]));

    if (currentDocIndex < selectedDocuments.length - 1) {
      setCurrentDocIndex((prev) => prev + 1);
    }
  }, [currentDocument, user?.providerId, currentDocIndex, selectedDocuments.length, dispatch]);

  const handleSignApprovedDocuments = useCallback(() => {
    if (!user?.providerId || approvedDocs.size === 0) return;
    setIsSignModalOpen(true);
  }, [user?.providerId, approvedDocs]);

  const handleSignConfirm = useCallback(
    async (signatureName: string, consentConfirmation: boolean, pin?: string) => {
      if (!user?.providerId) return;

      const documentsToUpdate = Array.from(approvedDocs);
      setDocumentsToSign(documentsToUpdate);
      setPhysPinError(null);

      // Reset previous success state BEFORE setting isSigningDocuments to true
      // This prevents stale isUpdateSuccess (from a prior APPROVED action) from
      // triggering the navigation useEffect immediately
      dispatch(resetUpdateDocumentStatuses());

      setIsSigningDocuments(true);

      try {
        dispatch(
          updateDocumentStatusesAction(
            user.providerId,
            documentsToUpdate,
            'SIGNED',
            signatureName,
            pin,
            consentConfirmation
          )
        );
        //  THIN-7681: keep the modal open until success (navigate) or a PIN-challenge error,
        //  so a wrong PIN can be retried inline.
      } catch (error) {
        setIsSigningDocuments(false);
        console.error('Error signing documents:', error);
      }
    },
    [user?.providerId, approvedDocs, dispatch]
  );

  useEffect(() => {
    if (!iframeRef) return;

    const interval = setInterval(async () => {
      await getAccessToken();
    }, 15000);

    return () => clearInterval(interval);
  }, [iframeRef]);

  useEffect(() => {
    if (selectedDocuments.length > 0) {
      console.log(selectedDocuments);
      const initialApprovedDocs = selectedDocuments
        .filter((doc) => compositeWorkflowStatusFromDocument(doc) === 'APPROVED')
        .map((doc) => doc.uuid);
      setApprovedDocs(new Set(initialApprovedDocs));
    }
  }, [selectedDocuments]);

  // Handle successful sign operation - navigate after completion
  useEffect(() => {
    if (updateDocumentStatuses?.isUpdateSuccess && isSigningDocuments && documentsToSign.length > 0) {
      // Clear loading state
      setIsSigningDocuments(false);
      
      // Navigate back to dashboard after successful signing
      navigate('/physician-dashboard', {
        state: {
          signedDocumentIds: documentsToSign,
        },
      });
      
      // Reset documentsToSign
      setDocumentsToSign([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateDocumentStatuses?.isUpdateSuccess, isSigningDocuments, navigate]);

  // Handle failed sign operation
  useEffect(() => {
    if (
      updateDocumentStatuses?.response &&
      !updateDocumentStatuses?.isUpdateSuccess &&
      updateDocumentStatuses?.response?.code !== 'OK' &&
      isSigningDocuments
    ) {
      //  THIN-7681: surface a signing-PIN challenge failure — keep the modal open to retry
      //  (WRONG_PIN / NO_PIN_SET) or show the lockout (LOCKED).
      const challenge = updateDocumentStatuses?.response?.data;
      const outcome = challenge?.outcome;
      if (outcome === 'LOCKED') {
        setPhysLocked({ open: true, until: challenge?.lockedUntil ?? null });
        setIsSignModalOpen(false);
      } else if (outcome === 'WRONG_PIN') {
        const left = challenge?.attemptsRemaining;
        setPhysPinError(
          `Incorrect PIN.${left != null ? ` ${left} attempt${left === 1 ? '' : 's'} remaining.` : ''}`
        );
      } else if (outcome === 'NO_PIN_SET') {
        setPhysPinError('No signing PIN set yet. Set one up in Profile → Signature & PIN.');
      }
      // Clear loading state on failure
      setIsSigningDocuments(false);
      setDocumentsToSign([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateDocumentStatuses?.response, updateDocumentStatuses?.isUpdateSuccess]);

  // Show warning toast (top-right) when one or more documents failed to sign
  const lastBulkSignWarningRef = useRef<string | null>(null);
  useEffect(() => {
    const response = updateDocumentStatuses?.response;
    if (!response) {
      lastBulkSignWarningRef.current = null;
      return;
    }
    const data = response?.data;
    const hasFailure = Array.isArray(data)
      ? data.some((d: any) => d?.isSuccessful === false)
      : data?.isSuccessful === false;
    if (hasFailure && response?.message) {
      const key = `${response?.requestId ?? ''}|${response?.message}`;
      if (lastBulkSignWarningRef.current !== key) {
        lastBulkSignWarningRef.current = key;
        toast(response.message, { icon: '⚠️', position: 'top-right' });
      }
    }
  }, [updateDocumentStatuses?.response]);

  const NavigationButtons = () => (
    <Stack
      direction="row"
      spacing={1}
      sx={{ ml: 2 }}
    >
      <Button
        size="small"
        disabled={currentDocIndex === 0}
        onClick={() => handleNavigate(0)}
      >
        <FirstPage />
      </Button>
      <Button
        size="small"
        disabled={currentDocIndex === 0}
        onClick={() => handleNavigate(currentDocIndex - 1)}
      >
        <ArrowBackIcon />
      </Button>
      <Button
        variant="contained"
        color="primary"
        sx={{ px: 2 }}
        onClick={handleApproveDocument}
        disabled={isCurrentDocumentApproved}
      >
        {isCurrentDocumentApproved ? 'Document Approved' : 'Approve Document'}
      </Button>
      <Button
        size="small"
        disabled={currentDocIndex === selectedDocuments.length - 1}
        onClick={() => handleNavigate(currentDocIndex + 1)}
      >
        <ArrowForwardIcon />
      </Button>
      <Button
        size="small"
        disabled={currentDocIndex === selectedDocuments.length - 1}
        onClick={() => handleNavigate(selectedDocuments.length - 1)}
      >
        <LastPage />
      </Button>
    </Stack>
  );

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        <Box
          sx={{
            position: 'relative',
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pl: 2 }}>
            <Typography variant="h5">Approval Pending</Typography>
            <Chip
              label={`for ${selectedDocuments.length - approvedDocs.size} / ${
                selectedDocuments.length
              } Documents`}
              color="primary"
              variant="outlined"
            />
            {selectedDocuments.length > -1 && <NavigationButtons />}
          </Box>
          <Box sx={{ pr: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSignApprovedDocuments}
              disabled={approvedDocs.size === 0 || isSigningDocuments}
              startIcon={isSigningDocuments ? <CircularProgress size={16} color="inherit" /> : null}
            >
              Sign Approved Documents
            </Button>
          </Box>
        </Box>

        <Link
          style={{
            color: '#111927',
            textDecoration: 'none',
            marginLeft: '1rem',
            marginBottom: '1rem',
            display: 'block',
          }}
          to={`${state?.source === 'PHYSICIAN' ? '/physician-dashboard' : '/dashboard'}`}
        >
          <Button
            sx={{
              color: '#111927',
              padding: '4px 8px',
              minWidth: 'unset',
            }}
            startIcon={<ArrowBackIcon sx={{ color: '#6C737F' }} />}
          >
            <Typography variant="body2">Back to Dashboard</Typography>
          </Button>
        </Link>
      </Box>

      <Box
        sx={{ height: 'calc(100% - 120px)' }}
        p={3}
      >
        <iframe
          src={currentDocument?.documentUrl || ''}
          width="100%"
          ref={updateIFrameRef}
          height="100%"
          style={{ border: 'none' }}
          title={`Preview document - ${currentDocument?.documentType}`}
        />
      </Box>
      <SignatureModal
        open={isSignModalOpen}
        onClose={() => {
          setIsSignModalOpen(false);
          setPhysPinError(null);
        }}
        onSign={handleSignConfirm}
        signatureName={signatureName}
        setSignatureName={setSignatureName}
        pinError={physPinError}
      />
      <SigningLockedDialog
        open={physLocked.open}
        lockedUntil={physLocked.until}
        onClose={() => setPhysLocked({ open: false, until: null })}
      />
    </>
  );
};

export default PhysicianDocumentsSign;
